import { useDeleteMachinesMutation, useFleetQuery } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import Action from '@/gf/components/Action'
import AddAccountMachineModal from '@/buyers/components/AddAccountMachineModal'
import Card from '@/gf/components/Card'
import Checkbox from '@/gf/components/Checkbox'
import DelayedSearch from '@/gf/components/inputs/DelayedSearch'
import Link from '@/gf/components/Link'
import Page from '@/gf/components/Page'
import Pagination from '@/gf/components/Pagination'
import Spinner from '@/gf/components/Spinner'
import { Td, Th, Tr } from '@/gf/components/Table'
import useMsgs from '@/gf/hooks/useMsgs'
import usePage from '@/gf/hooks/usePage'
import useSearchQueryParam from '@/gf/hooks/useSearchQueryParam'
import useToggle from '@/gf/hooks/useToggle'
import { CheckIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import Frame from '../components/Frame'

const breadcrumbs = {
  copy: 'Back to Dashboard',
  crumbs: [{ name: 'Fleet', href: '/orders' }],
}

const Fleet = () => {
  const [_, msgr] = useMsgs()
  const [page, setPage] = usePage()
  const { search, updateSearch } = useSearchQueryParam()
  const { orgId, organization } = useSession()
  const [addMachineOpen, machineToggle] = useToggle()
  const [deleting, setDeleting] = useState(false)
  const [selection, setSelection] = useState<string[]>([])
  const client = useGqlClient()
  const [deleteMachines] = useDeleteMachinesMutation({ client })

  const queryResult = useFleetQuery({
    variables: { orgId, page, filters: [['equals', 'accountId', organization.id]], search },
    client,
  })

  const loading = queryResult.loading
  const { paginatedOrgMachines } = queryResult.data || {}
  const machines = paginatedOrgMachines?.machines ?? []

  const check = (id: string) => setSelection([...selection, id])

  const unCheck = (id: string) => setSelection(selection.filter((sid) => sid !== id))

  const actions = [
    <Action.P onClick={machineToggle.on} key="add-new">
      Add Machine
    </Action.P>,
    <Link.S to="/fleet/import" key="import">
      Import
    </Link.S>,
  ]

  const closeAddMachine = () => {
    machineToggle.off()
    queryResult.refetch()
  }

  const onDelete = () => {
    if (deleting) return
    setDeleting(true)

    deleteMachines({ variables: { ids: selection } })
      .then(({ data }) => {
        queryResult.refetch()

        if (data?.deleteMachines) {
          // Deleting a unit is a soft-delete (it's hidden, not destroyed — its
          // history and name are retained and it can be restored), so the copy
          // says "removed from your active fleet", not "deleted" (GEA-6750).
          const { oks, errors } = data.deleteMachines
          const unit = (count: number) => (count === 1 ? 'unit was' : 'units were')
          let msg = `${oks} ${unit(oks)} removed from your active fleet.`

          if (errors > 0) {
            msg = `${msg} ${errors} ${unit(errors)} not removed due to being attached to an RFQ.`
          }

          msgr.add(msg, errors === 0 ? 'positive' : 'neutral')
        }

        setSelection([])
      })
      .catch(() => {
        msgr.addUnknownError()
      })
      .finally(() => setDeleting(false))
  }

  return (
    <Frame breadcrumbs={breadcrumbs}>
      <Page title="Fleet" actionsNext={actions}>
        <AddAccountMachineModal
          open={addMachineOpen}
          onClose={closeAddMachine}
          accountId={organization.id}
        />

        <div className="space-y-4 mt-4">
          <DelayedSearch
            value={search}
            setValue={(term) => {
              updateSearch(term)
              setPage(1)
            }}
          />

          {selection.length > 0 && (
            <div className="p-3 sm:px-6 sm:py-3 bg-gray-50">
              <div className="flex gap-2 items-center text-sm">
                <div>{selection.length.toLocaleString()} selected:</div>

                <Action.S onClick={onDelete} size="sm" performing={deleting}>
                  {deleting ? <>Deleting&hellip;</> : <>Delete</>}
                </Action.S>
              </div>
            </div>
          )}

          <Card className="overflow-auto">
            <table className="min-w-full divide-y-1 divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <Tr>
                  <Th />
                  <Th>Unit #</Th>
                  <Th>Serial Number</Th>
                  <Th>Make</Th>
                  <Th>Model</Th>
                  <Th>Year</Th>
                  <Th>Description</Th>
                  <Th className="text-center">Owned Asset</Th>
                  <Th>Engine Make</Th>
                  <Th>Engine Model</Th>
                  <Th>Engine Serial Number</Th>
                </Tr>
              </thead>
              <tbody
                className={`divide-y-1 divide-gray-200 ${loading && machines.length > 0 ? 'opacity-50 transition-opacity' : ''}`}
              >
                {loading && machines.length === 0 ? (
                  <Tr>
                    <Td colSpan={11}>
                      <div className="flex justify-center py-4">
                        <Spinner size="sm" />
                      </div>
                    </Td>
                  </Tr>
                ) : machines.length === 0 ? (
                  <Tr>
                    <Td colSpan={11}>No machines found.</Td>
                  </Tr>
                ) : (
                  machines.map((machine) => (
                    <Tr key={machine.name}>
                      <Td>
                        <Checkbox
                          checked={selection.includes(machine.id)}
                          setChecked={(checked) =>
                            checked ? check(machine.id) : unCheck(machine.id)
                          }
                        />
                      </Td>
                      <Td>
                        <Link.T to={`/machines/${machine.id}`}>{machine.name}</Link.T>
                      </Td>
                      <Td>{machine.serialNumber}</Td>
                      <Td>{machine.machine.make}</Td>
                      <Td>{machine.machine.model}</Td>
                      <Td>{machine.machine.year}</Td>
                      <Td className="truncate max-w-xs">{machine.description}</Td>
                      <Td className="flex justify-center">
                        {machine.owned && <CheckIcon className="w-5 h-5 text-green-500" />}
                      </Td>
                      <Td>{machine.engineMake}</Td>
                      <Td>{machine.engineModel}</Td>
                      <Td>{machine.engineSerialNumber}</Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </table>
          </Card>

          <Pagination
            pagination={paginatedOrgMachines?.pagination}
            page={page}
            updatePage={setPage}
          />
        </div>
      </Page>
    </Frame>
  )
}

export default Fleet
